export function chatNow(): number {
  return Math.floor(Date.now() / 1000);
}

export interface SelectedAgent {
  type: `agent` | `team` | `workflow`;
  id: string;
}

export interface FileTileModel {
  url: string;
  isUploading: boolean;
}

export interface RunResponse {
  content?: string | object;
  reasoning_content?: string;
  content_type: string;
  context?: MessageContext[];
  event: RunEvent;
  event_data?: object;
  messages?: ModelMessage[];
  metrics?: object;
  model?: string;
  run_id?: string;
  agent_id?: string;
  session_id?: string;
  tool?: ToolCall;
  tools?: Array<ToolCall>;
  created_at: number;
  extra_data?: PlaygroundAgentExtraData;
  images?: ImageData[];
  videos?: VideoData[];
  audio?: AudioData[];
  response_audio?: ResponseAudio;
}

export interface RunResponseContent {
  content?: string | object;
  content_type: string;
  context?: MessageContext[];
  event: RunEvent;
  event_data?: object;
  messages?: ModelMessage[];
  metrics?: object;
  model?: string;
  run_id?: string;
  agent_id?: string;
  session_id?: string;
  tool?: ToolCall;
  tools?: Array<ToolCall>;
  created_at: number;
  extra_data?: PlaygroundAgentExtraData;
  images?: ImageData[];
  videos?: VideoData[];
  audio?: AudioData[];
  response_audio?: ResponseAudio;
}

export interface AiChatMessage {
  run_id?: string;
  role: 'user' | 'agent' | 'system' | 'tool';
  content: string;
  streamingError?: boolean;
  created_at: number;
  tool_calls?: ToolCall[];
  extra_data?: {
    reasoning_steps?: ReasoningSteps[];
    reasoning_messages?: ReasoningMessage[];
    references?: ReferenceData[];
  };
  images?: ImageData[];
  videos?: VideoData[];
  audio?: AudioData[];
  response_audio?: ResponseAudio;

  session_data?: Record<string, string>;

  filesToUpload?: File[];
}

export enum RunEvent {
  RunStarted = `RunStarted`,
  RunContent = `RunContent`,
  RunCompleted = `RunCompleted`,
  RunError = `RunError`,
  RunOutput = `RunOutput`,
  UpdatingMemory = `UpdatingMemory`,
  ToolCallStarted = `ToolCallStarted`,
  ToolCallCompleted = `ToolCallCompleted`,
  MemoryUpdateStarted = `MemoryUpdateStarted`,
  MemoryUpdateCompleted = `MemoryUpdateCompleted`,
  ReasoningStarted = `ReasoningStarted`,
  ReasoningStep = `ReasoningStep`,
  ReasoningCompleted = `ReasoningCompleted`,
  RunCancelled = `RunCancelled`,
  RunPaused = `RunPaused`,
  RunContinued = `RunContinued`,
  // Team Events
  TeamRunStarted = `TeamRunStarted`,
  TeamRunContent = `TeamRunContent`,
  TeamRunCompleted = `TeamRunCompleted`,
  TeamRunError = `TeamRunError`,
  TeamRunCancelled = `TeamRunCancelled`,
  TeamToolCallStarted = `TeamToolCallStarted`,
  TeamToolCallCompleted = `TeamToolCallCompleted`,
  TeamReasoningStarted = `TeamReasoningStarted`,
  TeamReasoningStep = `TeamReasoningStep`,
  TeamReasoningCompleted = `TeamReasoningCompleted`,
  TeamMemoryUpdateStarted = `TeamMemoryUpdateStarted`,
  TeamMemoryUpdateCompleted = `TeamMemoryUpdateCompleted`,
}

export interface AgentExtraData {
  reasoning_steps?: ReasoningSteps[];
  reasoning_messages?: ReasoningMessage[];
  references?: ReferenceData[];
}

export interface PlaygroundAgentExtraData extends AgentExtraData {
  reasoning_messages?: ReasoningMessage[];
  references?: ReferenceData[];
}

interface ModelMessage {
  content: string | null;
  context?: MessageContext[];
  created_at: number;
  metrics?: {
    time: number;
    prompt_tokens: number;
    input_tokens: number;
    completion_tokens: number;
    output_tokens: number;
  };
  name: string | null;
  role: string;
  tool_args?: unknown;
  tool_call_id: string | null;
  tool_calls: Array<{
    function: {
      arguments: string;
      name: string;
    };
    id: string;
    type: string;
  }> | null;
}

interface MessageContext {
  query: string;
  docs?: Array<Record<string, object>>;
  time?: number;
}

export interface ToolCall {
  role: 'user' | 'tool' | 'system' | 'assistant';
  content: string | null;
  tool_call_id: string;
  tool_name: string;
  tool_args: Record<string, string>;
  tool_call_error: boolean;
  metrics: {
    time: number;
  };
  result?: string;
  created_at: number;
}

export interface ReasoningSteps {
  title: string;
  action?: string;
  result: string;
  reasoning: string;
  confidence?: number;
  next_action?: string;
}

export interface ReasoningMessage {
  role: 'user' | 'tool' | 'system' | 'assistant';
  content: string | null;
  tool_call_id?: string;
  tool_name?: string;
  tool_args?: Record<string, string>;
  tool_call_error?: boolean;
  metrics?: {
    time: number;
  };
  created_at?: number;
}

export interface ReferenceData {
  query: string;
  references: Reference[];
  time?: number;
}

export interface Reference {
  content: string;
  meta_data: {
    chunk: number;
    chunk_size: number;
  };
  name: string;
}

export interface ImageData {
  revised_prompt?: string;
  url: string;
  content?: string;
}

export interface VideoData {
  id: number;
  eta: number;
  url: string;
}

export interface AudioData {
  base64_audio?: string;
  mime_type?: string;
  url?: string;
  id?: string;
  content?: string;
  channels?: number;
  sample_rate?: number;
}

export interface ResponseAudio {
  id?: string;
  content?: string;
  transcript?: string;
  channels?: number;
  sample_rate?: number;
}
